// `extensions.code`, which the API classifies on (CLIENT_FAULT_CODES in
// apps/api/src/utils/graphqlOperationLog.ts) and the Apollo Client error link
// reads to decide whether to replay an operation with a fresh token.
// Distinct from GraphQLInternalErrorCode below — that one is
// `extensions.internalErrorCode`, a different field.
export enum GraphQLErrorCode {
  BAD_REQUEST = 'BAD_REQUEST',
  BAD_USER_INPUT = 'BAD_USER_INPUT',
  CONFLICT = 'CONFLICT',
  FORBIDDEN = 'FORBIDDEN',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  NOT_FOUND = 'NOT_FOUND',
  // The credentials were rejected — emitted ONLY by the API's Apollo context
  // function, which runs before any resolver. That exclusivity is load-bearing:
  // the client replays an operation carrying this code, and a replay is only
  // safe while nothing has executed server-side. Resolvers that refuse an
  // authenticated caller use UNAUTHORIZED or FORBIDDEN, never this.
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  UNAUTHORIZED = 'UNAUTHORIZED',
}

export enum GraphQLInternalErrorCode {
  // The session last verified a factor too long ago for this action; the web app asks Clerk to
  // reverify and retries (CASA 2.4.1).
  REVERIFICATION_REQUIRED = 'REVERIFICATION_REQUIRED',
  DOBOX_RESET_COOLDOWN = 'DOBOX_RESET_COOLDOWN',
  INBOX_ENTRY_DELETED = 'INBOX_ENTRY_DELETED',
  TASK_GROUP_OWNER_REMOVAL_FORBIDDEN = 'TASK_GROUP_OWNER_REMOVAL_FORBIDDEN',
  UNREADABLE_FILE_CONTENT = 'UNREADABLE_FILE_CONTENT',
  UNSUPPORTED_FILE_CONTENT = 'UNSUPPORTED_FILE_CONTENT',
  UNSUPPORTED_FILE_TYPE = 'UNSUPPORTED_FILE_TYPE',
  INVALID_LABEL_FOR_DOBOX_GENERATION = 'INVALID_LABEL_FOR_DOBOX_GENERATION',
  EMPTY_CONTENT_FOR_DOBOX_GENERATION = 'EMPTY_CONTENT_FOR_DOBOX_GENERATION',
  USER_NOT_MEMBER_OF_TEAM = 'USER_NOT_MEMBER_OF_TEAM',
  TEAM_INSTAGRAM_INTEGRATION_NOT_FOUND = 'TEAM_INSTAGRAM_INTEGRATION_NOT_FOUND',
  INSTAGRAM_CONVERSATION_NOT_FOUND = 'INSTAGRAM_CONVERSATION_NOT_FOUND',
  TEAM_FACEBOOK_INTEGRATION_NOT_FOUND = 'TEAM_FACEBOOK_INTEGRATION_NOT_FOUND',
  INSUFFICIENT_TEAM_ROLE_FOR_TRIGGER = 'INSUFFICIENT_TEAM_ROLE_FOR_TRIGGER',
  PERSON_CREATED_TRIGGER_NEVER_FIRES = 'PERSON_CREATED_TRIGGER_NEVER_FIRES',

  // Assistant image attachments
  ASSISTANT_IMAGE_UPLOAD_FILE_TYPE_NOT_PERMITTED = 'ASSISTANT_IMAGE_UPLOAD_FILE_TYPE_NOT_PERMITTED',
  ASSISTANT_IMAGE_UPLOAD_FILE_NAME_INVALID = 'ASSISTANT_IMAGE_UPLOAD_FILE_NAME_INVALID',
  ASSISTANT_IMAGE_UPLOAD_INFECTED = 'ASSISTANT_IMAGE_UPLOAD_INFECTED',
  ASSISTANT_IMAGE_UPLOAD_INVALID_TYPE = 'ASSISTANT_IMAGE_UPLOAD_INVALID_TYPE',
  ASSISTANT_IMAGE_UPLOAD_SCAN_PENDING = 'ASSISTANT_IMAGE_UPLOAD_SCAN_PENDING',
  ASSISTANT_TOO_MANY_IMAGES = 'ASSISTANT_TOO_MANY_IMAGES',

  // Generated Images — batch query
  GENERATED_IMAGES_TOO_MANY_IDS = 'GENERATED_IMAGES_TOO_MANY_IDS',

  // Flows — Steps
  FLOW_STEPS_EMPTY = 'FLOW_STEPS_EMPTY',

  // Flows — AI generation ended without a usable outcome
  FLOW_GENERATION_REFUSED = 'FLOW_GENERATION_REFUSED',
  FLOW_GENERATION_OUTPUT_TRUNCATED = 'FLOW_GENERATION_OUTPUT_TRUNCATED',
  FLOW_GENERATION_ENDED_UNEXPECTEDLY = 'FLOW_GENERATION_ENDED_UNEXPECTEDLY',

  // Flows — a strict flow was blocked before step 0 because a variable cannot resolve
  FLOW_REFERENCES_UNRESOLVED = 'FLOW_REFERENCES_UNRESOLVED',

  // Company Brain — automatic discovery
  BRAIN_DISCOVERY_BLUEPRINT_EXISTS = 'BRAIN_DISCOVERY_BLUEPRINT_EXISTS',
  BRAIN_DISCOVERY_IN_PROGRESS = 'BRAIN_DISCOVERY_IN_PROGRESS',
  BRAIN_DISCOVERY_NO_SOURCES = 'BRAIN_DISCOVERY_NO_SOURCES',
  BRAIN_DISCOVERY_UNKNOWN_CHANNEL = 'BRAIN_DISCOVERY_UNKNOWN_CHANNEL',
  BRAIN_DISCOVERY_NO_CALENDAR = 'BRAIN_DISCOVERY_NO_CALENDAR',

  // Knowledge — Spaces
  KNOWLEDGE_SPACE_NOT_FOUND = 'KNOWLEDGE_SPACE_NOT_FOUND',
  KNOWLEDGE_SYSTEM_SPACE_NOT_DELETABLE = 'KNOWLEDGE_SYSTEM_SPACE_NOT_DELETABLE',
  KNOWLEDGE_SYSTEM_SPACE_NOT_RENAMABLE = 'KNOWLEDGE_SYSTEM_SPACE_NOT_RENAMABLE',
  KNOWLEDGE_SPACE_AT_CAPACITY = 'KNOWLEDGE_SPACE_AT_CAPACITY',
  KNOWLEDGE_SPACE_OWNER_ID_INPUT_INVALID = 'KNOWLEDGE_SPACE_OWNER_ID_INPUT_INVALID',
  KNOWLEDGE_SPACE_NAME_INVALID = 'KNOWLEDGE_SPACE_NAME_INVALID',
  KNOWLEDGE_SPACE_OWNER_NOT_FOUND = 'KNOWLEDGE_SPACE_OWNER_NOT_FOUND',

  // Knowledge — PBAC
  KNOWLEDGE_SPACE_FORBIDDEN = 'KNOWLEDGE_SPACE_FORBIDDEN',
  KNOWLEDGE_SPACE_PARTICIPANT_ALREADY_EXISTS = 'KNOWLEDGE_SPACE_PARTICIPANT_ALREADY_EXISTS',
  KNOWLEDGE_SPACE_PARTICIPANT_IS_OWNER = 'KNOWLEDGE_SPACE_PARTICIPANT_IS_OWNER',
  KNOWLEDGE_SPACE_NOT_OWNER = 'KNOWLEDGE_SPACE_NOT_OWNER',
  KNOWLEDGE_SPACE_PARTICIPANTS_NOT_ALLOWED_ON_USER_SPACE = 'KNOWLEDGE_SPACE_PARTICIPANTS_NOT_ALLOWED_ON_USER_SPACE',
  KNOWLEDGE_SPACE_INVALID_PARTICIPANT_ROLE = 'KNOWLEDGE_SPACE_INVALID_PARTICIPANT_ROLE',
  KNOWLEDGE_SPACE_VERSION_CONFLICT = 'KNOWLEDGE_SPACE_VERSION_CONFLICT',
  KNOWLEDGE_SPACE_VISIBILITY_REQUIRED = 'KNOWLEDGE_SPACE_VISIBILITY_REQUIRED',
  KNOWLEDGE_SPACE_CANNOT_LEAVE_TEAM_SPACE = 'KNOWLEDGE_SPACE_CANNOT_LEAVE_TEAM_SPACE',

  // Knowledge — Pages
  KNOWLEDGE_SYSTEM_PAGE_NOT_DELETABLE = 'KNOWLEDGE_SYSTEM_PAGE_NOT_DELETABLE',
  KNOWLEDGE_SYSTEM_PAGE_NOT_RENAMABLE = 'KNOWLEDGE_SYSTEM_PAGE_NOT_RENAMABLE',
  KNOWLEDGE_SYSTEM_PAGE_NOT_MOVABLE = 'KNOWLEDGE_SYSTEM_PAGE_NOT_MOVABLE',
  KNOWLEDGE_SYSTEM_PAGE_READ_ONLY = 'KNOWLEDGE_SYSTEM_PAGE_READ_ONLY',
  KNOWLEDGE_SYSTEM_PAGE_NO_CHILDREN = 'KNOWLEDGE_SYSTEM_PAGE_NO_CHILDREN',
  KNOWLEDGE_SPACE_MANAGED_BY_BRAIN = 'KNOWLEDGE_SPACE_MANAGED_BY_BRAIN',
  KNOWLEDGE_PAGE_NOT_FOUND = 'KNOWLEDGE_PAGE_NOT_FOUND',
  KNOWLEDGE_PAGE_PARENT_NOT_FOUND = 'KNOWLEDGE_PAGE_PARENT_NOT_FOUND',
  KNOWLEDGE_PAGE_DEPTH_EXCEEDED = 'KNOWLEDGE_PAGE_DEPTH_EXCEEDED',
  KNOWLEDGE_PAGE_CROSS_SPACE_PARENT = 'KNOWLEDGE_PAGE_CROSS_SPACE_PARENT',
  KNOWLEDGE_PAGE_VERSION_CONFLICT = 'KNOWLEDGE_PAGE_VERSION_CONFLICT',
  KNOWLEDGE_PAGE_MOVE_NOT_SUPPORTED = 'KNOWLEDGE_PAGE_MOVE_NOT_SUPPORTED',
  KNOWLEDGE_PAGE_MOVE_CROSS_OWNER = 'KNOWLEDGE_PAGE_MOVE_CROSS_OWNER',
  KNOWLEDGE_PAGE_MOVE_NO_OP = 'KNOWLEDGE_PAGE_MOVE_NO_OP',
  KNOWLEDGE_PAGE_MOVE_TARGET_PARENT_INVALID = 'KNOWLEDGE_PAGE_MOVE_TARGET_PARENT_INVALID',
  KNOWLEDGE_PAGE_MOVE_EXCEEDS_DEPTH = 'KNOWLEDGE_PAGE_MOVE_EXCEEDS_DEPTH',
  KNOWLEDGE_PAGE_MOVE_TOO_LARGE = 'KNOWLEDGE_PAGE_MOVE_TOO_LARGE',
  KNOWLEDGE_PAGE_MOVE_IN_PROGRESS = 'KNOWLEDGE_PAGE_MOVE_IN_PROGRESS',
  KNOWLEDGE_PAGE_CONTENT_REJECTED = 'KNOWLEDGE_PAGE_CONTENT_REJECTED',
  KNOWLEDGE_PAGE_TITLE_INVALID = 'KNOWLEDGE_PAGE_TITLE_INVALID',
  KNOWLEDGE_PAGE_TITLE_UNREADABLE = 'KNOWLEDGE_PAGE_TITLE_UNREADABLE',

  // Knowledge — Storage
  KNOWLEDGE_S3_WRITE_FAILED = 'KNOWLEDGE_S3_WRITE_FAILED',

  // Knowledge — Diff
  KNOWLEDGE_PAGE_DIFF_TOO_LARGE = 'KNOWLEDGE_PAGE_DIFF_TOO_LARGE',

  // Knowledge — Triggers
  KNOWLEDGE_TRIGGER_OWNERSHIP_IMMUTABLE = 'KNOWLEDGE_TRIGGER_OWNERSHIP_IMMUTABLE',

  // Knowledge — Attachments / Images
  KNOWLEDGE_ATTACHMENT_INFECTED = 'KNOWLEDGE_ATTACHMENT_INFECTED',
  KNOWLEDGE_ATTACHMENT_INVALID_TYPE = 'KNOWLEDGE_ATTACHMENT_INVALID_TYPE',
  KNOWLEDGE_ATTACHMENT_NOT_FOUND = 'KNOWLEDGE_ATTACHMENT_NOT_FOUND',
  KNOWLEDGE_SCAN_RESULT_NOT_FOUND = 'KNOWLEDGE_SCAN_RESULT_NOT_FOUND',

  // Knowledge — Favorites
  KNOWLEDGE_FAVORITES_LIMIT_REACHED = 'KNOWLEDGE_FAVORITES_LIMIT_REACHED',

  // CRM — People
  CRM_PERSON_NOT_FOUND = 'CRM_PERSON_NOT_FOUND',
  CRM_PERSON_NOT_OWNED = 'CRM_PERSON_NOT_OWNED',
  CRM_TEAM_FORBIDDEN = 'CRM_TEAM_FORBIDDEN',
  CRM_PERSON_EMAILS_INVALID = 'CRM_PERSON_EMAILS_INVALID',
  CRM_PERSON_EMAIL_CONFLICT = 'CRM_PERSON_EMAIL_CONFLICT',
  CRM_PERSON_EMAILS_CONCURRENT_UPDATE = 'CRM_PERSON_EMAILS_CONCURRENT_UPDATE',
  CRM_PERSON_PHONES_INVALID = 'CRM_PERSON_PHONES_INVALID',
  CRM_PERSON_CONTACTS_LIMIT = 'CRM_PERSON_CONTACTS_LIMIT',
  CRM_PERSON_SHARE_LIMIT = 'CRM_PERSON_SHARE_LIMIT',
  CRM_PERSON_SHARE_CONCURRENT_UPDATE = 'CRM_PERSON_SHARE_CONCURRENT_UPDATE',
  CRM_PERSON_MERGE_LIMIT = 'CRM_PERSON_MERGE_LIMIT',
  CRM_PERSON_DISPLAY_NAME_REQUIRED = 'CRM_PERSON_DISPLAY_NAME_REQUIRED',
  CRM_PERSON_FIELD_TOO_LONG = 'CRM_PERSON_FIELD_TOO_LONG',
  CRM_PERSON_NOT_MATERIALIZED_YET = 'CRM_PERSON_NOT_MATERIALIZED_YET',
  CRM_PERSON_CONCURRENT_UPDATE = 'CRM_PERSON_CONCURRENT_UPDATE',
  CRM_PERSON_EVENT_NOT_FOUND = 'CRM_PERSON_EVENT_NOT_FOUND',
  CRM_PERSON_EVENT_TITLE_REQUIRED = 'CRM_PERSON_EVENT_TITLE_REQUIRED',

  // DoBox — related entry links
  RELATED_DOBOX_SELF_LINK = 'RELATED_DOBOX_SELF_LINK',
  RELATED_DOBOX_NOT_IN_TASK_GROUP = 'RELATED_DOBOX_NOT_IN_TASK_GROUP',
  RELATED_DOBOX_LIMIT_EXCEEDED = 'RELATED_DOBOX_LIMIT_EXCEEDED',

  // Team — consultant offboarding
  CONSULTANT_OFFBOARD_PARTIAL = 'CONSULTANT_OFFBOARD_PARTIAL',

  // Credits — purchase
  CREDIT_PACK_INVALID_LOOKUP_KEY = 'CREDIT_PACK_INVALID_LOOKUP_KEY',
  CREDIT_PACK_PRICE_NOT_FOUND = 'CREDIT_PACK_PRICE_NOT_FOUND',
  CREDIT_PACK_PRICE_MISSING_CREDITS_METADATA = 'CREDIT_PACK_PRICE_MISSING_CREDITS_METADATA',

  // DoBox — Executor
  DOBOX_EXECUTOR_DISABLED = 'DOBOX_EXECUTOR_DISABLED',
  EXECUTOR_APPROVAL_REQUIRES_CLICK = 'EXECUTOR_APPROVAL_REQUIRES_CLICK',
  EXECUTOR_ITEM_EXPIRED = 'EXECUTOR_ITEM_EXPIRED',
  EXECUTOR_ITEM_SUPERSEDED = 'EXECUTOR_ITEM_SUPERSEDED',
  EXECUTOR_ITEM_STALE = 'EXECUTOR_ITEM_STALE',
  EXECUTOR_ITEM_NOT_ACTIONABLE = 'EXECUTOR_ITEM_NOT_ACTIONABLE',
  EXECUTOR_CONVERSATION_NOT_CLEARABLE = 'EXECUTOR_CONVERSATION_NOT_CLEARABLE',
  PLAN_ITEM_STALE = 'PLAN_ITEM_STALE',
  PLAN_SLOT_MISSING = 'PLAN_SLOT_MISSING',
  DELEGATION_PLAN_CHANGED = 'DELEGATION_PLAN_CHANGED',
  DELEGATION_ATTACHMENTS_UNSUPPORTED = 'DELEGATION_ATTACHMENTS_UNSUPPORTED',
  DELEGATION_DISPATCH_IN_PROGRESS = 'DELEGATION_DISPATCH_IN_PROGRESS',
  AGENT_RUN_LIMIT_REACHED = 'AGENT_RUN_LIMIT_REACHED',
  AGENT_RUNS_ITEM_TOO_LARGE = 'AGENT_RUNS_ITEM_TOO_LARGE',
  LOCAL_RUN_ALREADY_ACTIVE_ON_DEVICE = 'LOCAL_RUN_ALREADY_ACTIVE_ON_DEVICE',
  TERMINAL_ATTACHMENT_SIZE_INVALID = 'TERMINAL_ATTACHMENT_SIZE_INVALID',
  TERMINAL_ATTACHMENT_TYPE_NOT_ALLOWED = 'TERMINAL_ATTACHMENT_TYPE_NOT_ALLOWED',
  TERMINAL_DEVICE_OFFLINE = 'TERMINAL_DEVICE_OFFLINE',
  TERMINAL_SUMMARY_FAILED = 'TERMINAL_SUMMARY_FAILED',
  TERMINAL_MOVE_HANDLE_INVALID = 'TERMINAL_MOVE_HANDLE_INVALID',
  TERMINAL_MOVE_UNDO_EXPIRED = 'TERMINAL_MOVE_UNDO_EXPIRED',
  TERMINAL_FINISH_UNAVAILABLE = 'TERMINAL_FINISH_UNAVAILABLE',
  CONTINUE_IN_TERMINAL_UNAVAILABLE = 'CONTINUE_IN_TERMINAL_UNAVAILABLE',
  DELEGATION_HELD_BY_TERMINAL = 'DELEGATION_HELD_BY_TERMINAL',
  TERMINAL_MESSAGE_INVALID = 'TERMINAL_MESSAGE_INVALID',
  TERMINAL_MESSAGE_TOO_LARGE = 'TERMINAL_MESSAGE_TOO_LARGE',
  EXECUTOR_RUN_ALREADY_ACTIVE = 'EXECUTOR_RUN_ALREADY_ACTIVE',
  CAPABILITY_RESUME_EXHAUSTED = 'CAPABILITY_RESUME_EXHAUSTED',
  // AS-05: approveAgentTrial's own re-validation of an edited limit-param
  // value against the template's AgentToolPolicyLimit — distinct from the
  // worker-side agentToolPolicyGuard.ts errorCode of the same name, which
  // rides in an ExecutorToolOutput, never a GraphQL extension.
  AGENT_TOOL_LIMIT_EXCEEDED = 'AGENT_TOOL_LIMIT_EXCEEDED',
  CAPABILITY_STILL_MISSING = 'CAPABILITY_STILL_MISSING',
  NOT_DOBOX_OWNER = 'NOT_DOBOX_OWNER',
  AGENT_OUTBOUND_BOT_CANNOT_USE_DIRECT_MESSAGE = 'AGENT_OUTBOUND_BOT_CANNOT_USE_DIRECT_MESSAGE',
  AGENT_PEOPLE_MESSAGING_DISABLED = 'AGENT_PEOPLE_MESSAGING_DISABLED',
  AGENT_PEOPLE_SCOPE_NEEDS_NOTIFICATIONS = 'AGENT_PEOPLE_SCOPE_NEEDS_NOTIFICATIONS',

  // DoBox task tree
  TASK_TREE_MOVE_BLOCKED = 'TASK_TREE_MOVE_BLOCKED',
  TASK_TREE_CYCLE = 'TASK_TREE_CYCLE',
  TASK_TREE_DEPTH_EXCEEDED = 'TASK_TREE_DEPTH_EXCEEDED',
  TASK_TREE_SIZE_EXCEEDED = 'TASK_TREE_SIZE_EXCEEDED',
}

export const FAILED_ATTACHMENTS_EXTENSION_KEY = 'failedAttachments';
